import { ChevronRightIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { FC } from "react";
import { PegaApplication } from "../journey/pega/apiGetPegaState.ts";

type Props = {
  applications: PegaApplication[];
  locale?: string;
  onSelectApplication: (application: PegaApplication) => void;
  onStartNew: () => void;
};

function formatSavedDate(iso: string, locale?: string): string | null {
  if (!iso?.trim()) {
    return null;
  }

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const dateOptions: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
  };
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };

  const day = locale
    ? date.toLocaleDateString(locale, dateOptions)
    : date.toLocaleDateString(undefined, dateOptions);
  const time = (locale
    ? date.toLocaleTimeString(locale, timeOptions)
    : date.toLocaleTimeString(undefined, timeOptions)
  )
    .toLowerCase()
    .replace(" ", "");
  return `Saved on ${day} ${time}`;
}

function getApplicationLabel(application: PegaApplication): string {
  return application.productDescription?.trim() || application.productName?.trim() || "Application";
}

const InProgressApplicationsModal: FC<Props> = ({
  applications,
  locale,
  onSelectApplication,
  onStartNew,
}) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-sm mx-4 px-6 pt-8 pb-8 flex flex-col max-h-[90vh]">
        <h2 className="text-2xl font-bold text-content-primary mb-3">
          You have an application in progress
        </h2>

        <p className="text-sm text-content-secondary mb-6">
          Select an application to continue, or start a new one?
        </p>

        <div className="flex flex-col gap-2 mb-6 overflow-y-auto">
          {applications.map((application) => {
            const savedDate = formatSavedDate(application.updateDateTime, locale);

            return (
              <button
                key={application.applicationAssignmentKey}
                type="button"
                onClick={() => onSelectApplication(application)}
                className="w-full flex items-center gap-3 p-4 rounded-lg border border-azure-20 bg-azure-10 text-left cursor-pointer hover:bg-azure-20/60 transition-colors"
              >
                <DocumentTextIcon className="w-5 h-5 flex-shrink-0 text-[#3B5BDB]" />
                <div className="flex flex-col flex-1 min-w-0 gap-0.5">
                  <span className="text-sm font-semibold text-[#3B5BDB] leading-5">
                    {getApplicationLabel(application)}
                  </span>
                  {savedDate && (
                    <span className="text-xs text-azure-70 leading-4">
                      {savedDate}
                    </span>
                  )}
                </div>
                <ChevronRightIcon className="w-4 h-4 flex-shrink-0 text-[#3B5BDB]" />
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={onStartNew}
          className="w-full py-4 px-4 bg-[#3B5BDB] hover:bg-[#2F4BC4] active:bg-[#2439A8] cursor-pointer transition-colors !text-white rounded-full font-medium text-base"
        >
          Start a new application
        </button>
      </div>
    </div>
  );
};

export default InProgressApplicationsModal;
